/**
 * @file Pure view-logic for the admin command palette (⌘K). Builds the
 *       command catalog, filters it against the query string, and groups
 *       the result for rendering. Extracted from the JSX so mocha can
 *       exercise it without a DOM (mirrors the `*_view.ts` pattern used
 *       by the social / settings / courses panels).
 * @module @labcraft/admin/ui/kernel/cmdk-view
 *
 * The palette is descriptor-driven: this module deals only in inert
 * `CmdkCommand` records (id + label + optional href/hint). Dispatch —
 * navigating to a page href, flipping density, signing out — happens in
 * {@link CmdkPalette} keyed on `command.id`, so the catalog stays pure.
 */

/** Whether a command navigates to a route or runs a shell action. */
export type CmdkKind = 'page' | 'action'

/**
 * One selectable row in the palette.
 *
 * @property id - Stable identity; `CmdkPalette` switches dispatch on it.
 * @property kind - `'page'` (navigates `href`) or `'action'` (shell verb).
 * @property label - Visible row text; also the substring-match target.
 * @property href - Route target; present iff `kind === 'page'`.
 * @property hint - Right-aligned affordance text (e.g. a shortcut), or ''.
 */
export type CmdkCommand = {
  id: string
  kind: CmdkKind
  label: string
  href?: string
  hint?: string
}

/**
 * A rendered grouping (heading + its commands), in display order.
 *
 * @property heading - Uppercase group label (`Pages` / `Actions`).
 * @property commands - The group's commands, catalog order preserved.
 */
export type CmdkGroup = {
  heading: string
  commands: CmdkCommand[]
}

/**
 * A navigable destination fed in from the shell's panel list.
 *
 * @property label - Human label (e.g. `Learners`).
 * @property href - Root-relative route (e.g. `/learners`).
 */
export type CmdkPage = {
  label: string
  href: string
}

/**
 * Shell-level actions always offered by the palette. Limited to
 * admin-owned state — density is a kernel concern, sign-out is shell
 * chrome. Theme is deliberately absent: it moved to the shared
 * cookie-backed toggle and is no longer an admin concern.
 */
export const ADMIN_ACTIONS: readonly CmdkCommand[] = [
  { id: 'toggle-density', kind: 'action', label: 'Toggle density', hint: '' },
  { id: 'sign-out', kind: 'action', label: 'Sign out', hint: '' },
]

/**
 * Build the full command catalog: one `page` command per nav
 * destination, then the static shell `action` commands.
 *
 * @param pages - The shell's panel destinations (label + href).
 * @returns Catalog in display order (pages first, then actions).
 */
export function buildCatalog(pages: readonly CmdkPage[]): CmdkCommand[] {
  const pageCommands: CmdkCommand[] = pages.map(p => ({
    id: `page:${p.href}`,
    kind: 'page',
    label: p.label,
    href: p.href,
  }))
  return [...pageCommands, ...ADMIN_ACTIONS]
}

/**
 * Case-insensitive substring filter on the command label. An empty or
 * whitespace-only query returns the catalog unchanged.
 *
 * @param catalog - The full command list.
 * @param query - The raw input string.
 * @returns The subset whose label contains the trimmed query.
 */
export function filterCommands(
  catalog: readonly CmdkCommand[],
  query: string,
): CmdkCommand[] {
  const needle = query.trim().toLowerCase()
  if (needle === '') return [...catalog]
  return catalog.filter(c => c.label.toLowerCase().includes(needle))
}

/**
 * Split a flat command list into display groups (`Pages`, `Actions`),
 * preserving catalog order within each and dropping empty groups.
 *
 * @param commands - The (already filtered) flat command list.
 * @returns Non-empty groups in display order.
 */
export function groupCommands(commands: readonly CmdkCommand[]): CmdkGroup[] {
  const pages = commands.filter(c => c.kind === 'page')
  const actions = commands.filter(c => c.kind === 'action')
  const groups: CmdkGroup[] = []
  if (pages.length > 0) groups.push({ heading: 'Pages', commands: pages })
  if (actions.length > 0) groups.push({ heading: 'Actions', commands: actions })
  return groups
}

/**
 * Clamp a selection index into `[0, length - 1]`, returning `0` when the
 * list is empty. Keeps arrow-key navigation in range as the filtered
 * list shrinks under the selection.
 *
 * @param index - The desired index.
 * @param length - The current flat-list length.
 * @returns The clamped index.
 */
export function clampIndex(index: number, length: number): number {
  if (length <= 0) return 0
  if (index < 0) return 0
  if (index > length - 1) return length - 1
  return index
}
